import {AxiosError} from 'axios';
import BASE_URL from '../../config/env';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  IAIInvestigateConversationDocumentDetailsPayload,
  IAIInvestigateConversationDocumentDetailsResponse,
  IAIInvestigateIndicesDetailsPayload,
  IAIInvestigateIndicesPayload,
  IAIInvestigateTranscriptDetailsResponse,
  IAIInvestigateTranscriptListResponse,
  IAIInvestigateTranscriptPayload,
  ICreateConversationPayload,
  ICreateConversationResponse,
  ICreateIndexPayload,
  ICreateIndexResponse,
  ICreateTranscriptPayload,
  ICreateTranscriptResponse,
  IDeleteTranscriptResponse,
  IEditIndexPayload,
  IRetryInvestigateIndexResponse,
  IToggleIndexPayload,
  IToggleIndexResponse,
  IGetDownloadDocumentResponse,
  IUpdateTranscriptPayload,
  IUpdateTranscriptResponse,
  IIndexSourceDocumentCountPayload,
  IIndexSourceDocumentCountResponse,
  IAIInvestigateDocumentSummaryResponse,
  IInvestigateNavigationMasterListPayload,
  IInvestigateNavigationMasterListResponse,
} from '../types/aiInvestigate.type';

const MAX_AI_RESPONSE_TIME = 1000 * 100; // 100 seconds

const aiInvestigateIndicesEndpoint = (path: string | number) =>
  `${BASE_URL}/ai/api/v1/investigate${path}`;

const aiInvestigateTranscriptEndpoint = (path: string | number) =>
  `${BASE_URL}/ai/api/v1/investigate/transcripts${path}`;

const aiInvestigateDetailsEndpoint = (
  indexId: string | number,
  path: string | number
) => `${BASE_URL}/ai/api/v1/investigate/indices/${indexId}/details/${path}`;

const aiInvestigateEndpoint = (path: string | number) =>
  `${BASE_URL}/ai/api/v1/investigate/indices/${path}`;

export const getAIInvestigateIndicesList = async (
  payload: IAIInvestigateIndicesPayload
) => {
  const {data} = await apiClient.post(
    aiInvestigateIndicesEndpoint('/indices/list'),
    payload
  );
  return data;
};

export const getAIInvestigateTranscriptList = async (
  payload: IAIInvestigateTranscriptPayload
): Promise<IAIInvestigateTranscriptListResponse> => {
  const {data} = await apiClient.post<IAIInvestigateTranscriptListResponse>(
    aiInvestigateTranscriptEndpoint('/list'),
    payload
  );
  return data;
};

export const createInvestigateTranscript = async (
  payload: ICreateTranscriptPayload
): Promise<ICreateTranscriptResponse> => {
  const {data} = await apiClient.post<ICreateTranscriptResponse>(
    aiInvestigateTranscriptEndpoint(''),
    payload
  );
  return data;
};

export const updateInvestigateTranscript = async (
  transcriptId: number | string,
  payload: IUpdateTranscriptPayload
): Promise<IUpdateTranscriptResponse> => {
  const {data} = await apiClient.put<IUpdateTranscriptResponse>(
    aiInvestigateTranscriptEndpoint(`/${transcriptId}`),
    payload
  );
  return data;
};

export const createInvestigateIndex = async (
  payload: ICreateIndexPayload
): Promise<ICreateIndexResponse> => {
  const {data} = await apiClient.post<ICreateIndexResponse>(
    aiInvestigateIndicesEndpoint('/indices'),
    payload
  );
  return data;
};

export const deleteInvestigateTranscript = async (
  transcriptId: number | string
): Promise<IDeleteTranscriptResponse> => {
  const {data} = await apiClient.delete<IDeleteTranscriptResponse>(
    aiInvestigateTranscriptEndpoint(`/${transcriptId}`)
  );
  return data;
};

export const deleteInvestigateConversation = async (
  transcriptId: number | string,
  conversationId: number | string
): Promise<void> => {
  await apiClient.delete(
    aiInvestigateTranscriptEndpoint(
      `/${transcriptId}/conversations/${conversationId}`
    )
  );
};

export const getAIInvestigateOwnersList = async () => {
  const {data} = await apiClient.get(
    aiInvestigateTranscriptEndpoint('/owners')
  );
  return data;
};

export const getAIInvestigateTranscriptDetails = async (
  transcriptId: number | string
): Promise<IAIInvestigateTranscriptDetailsResponse> => {
  const {data} = await apiClient.get<IAIInvestigateTranscriptDetailsResponse>(
    aiInvestigateTranscriptEndpoint(`/${transcriptId}`)
  );
  return data;
};
export const createInvestigateConversation = async (
  transcriptId: number,
  payload: ICreateConversationPayload
): Promise<ICreateConversationResponse> => {
  const {data} = await apiClient.post<ICreateConversationResponse>(
    aiInvestigateTranscriptEndpoint(`/${transcriptId}/conversation`),
    payload,
    {timeout: MAX_AI_RESPONSE_TIME}
  );
  return data;
};
export const toggleIndex = async (
  indexId: number | string,
  payload: IToggleIndexPayload
): Promise<IToggleIndexResponse> => {
  const {data} = await apiClient.post<IToggleIndexResponse>(
    aiInvestigateIndicesEndpoint(`/indices/${indexId}/switchstatus`),
    payload
  );
  return data;
};

export const retryInvestigateIndex = async (
  indexId: number | string,
  indexDetailsId: number | string
): Promise<IRetryInvestigateIndexResponse> => {
  const {data} = await apiClient.post<IRetryInvestigateIndexResponse>(
    aiInvestigateIndicesEndpoint(
      `/indices/${indexId}/details/${indexDetailsId}/retry`
    ),
    {}
  );
  return data;
};

export const getCsvDownloadForManageIndices = async (
  queryParams?: Record<string, any>
): Promise<IGetDownloadDocumentResponse> => {
  try {
    const response = await apiClient.get(aiInvestigateEndpoint('download'), {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
      params: queryParams,
    });

    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename\*=UTF-8''(.+)$/);
    const filename = match ? decodeURIComponent(match[1]) : 'document.csv';

    return {
      blob: response.data,
      filename,
      contentDisposition: disposition,
    };
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const getCsvDownloadForIndexDetail = async (
  indexId: number | string
): Promise<IGetDownloadDocumentResponse> => {
  try {
    const response = await apiClient.get(
      aiInvestigateDetailsEndpoint(indexId, 'download'),
      {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      }
    );

    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename\*=UTF-8''(.+)$/);
    const filename = match ? decodeURIComponent(match[1]) : 'document.csv';

    return {
      blob: response.data,
      filename,
      contentDisposition: disposition,
    };
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};
export const getAIInvestigateIndicesDetails = async (
  indexId: string | number,
  payload: IAIInvestigateIndicesDetailsPayload
) => {
  const {data} = await apiClient.post(
    aiInvestigateIndicesEndpoint(`/indices/${indexId}/details`),
    payload
  );
  return data;
};

export const getSavedSearchList = async (isCountIncluded: boolean) => {
  const {data} = await apiClient.post(
    aiInvestigateIndicesEndpoint(`/indices/savedsearchlist`),
    {isCountIncluded: isCountIncluded}
  );
  return data;
};

export const getAIInvestigateConversationDocumentDetails = async (
  transcriptId: number | string,
  conversationId: number | string,
  payload: IAIInvestigateConversationDocumentDetailsPayload
): Promise<IAIInvestigateConversationDocumentDetailsResponse> => {
  const {data} =
    await apiClient.post<IAIInvestigateConversationDocumentDetailsResponse>(
      aiInvestigateIndicesEndpoint(
        `/transcripts/${transcriptId}/conversations/${conversationId}`
      ),
      payload
    );
  return data;
};

export const editInvestigateIndex = async (
  indexId: number | string,
  payload: IEditIndexPayload
): Promise<ICreateIndexResponse> => {
  const {data} = await apiClient.put<ICreateIndexResponse>(
    aiInvestigateIndicesEndpoint(`/indices/${indexId}`),
    payload
  );
  return data;
};

export const getAIInvestigateIndexSourceDocumentCount = async (
  payload: IIndexSourceDocumentCountPayload
): Promise<IIndexSourceDocumentCountResponse> => {
  const {data} = await apiClient.post<IIndexSourceDocumentCountResponse>(
    aiInvestigateIndicesEndpoint('/indices/documentscount'),
    payload
  );
  return data;
};

export const createInvestigateNavigationMasterList = async (
  payload: IInvestigateNavigationMasterListPayload
): Promise<IInvestigateNavigationMasterListResponse> => {
  const {data} = await apiClient.post<IInvestigateNavigationMasterListResponse>(
    aiInvestigateIndicesEndpoint('/navigation-master-list'),
    payload
  );
  return data;
};

export const downloadTranscriptConversation = async (transcriptId: number) => {
  const response = await apiClient.get(
    aiInvestigateTranscriptEndpoint(`/${transcriptId}/bgdownload`)
  );
  return response;
};

export const getAIInvestigateDocumentSummary = async (
  indexId: number,
  documentId: number,
  docId: string
): Promise<IAIInvestigateDocumentSummaryResponse> => {
  const {data} = await apiClient.get<IAIInvestigateDocumentSummaryResponse>(
    aiInvestigateIndicesEndpoint(`/${indexId}/document/${documentId}/summary?documentId=${docId}`),
    {timeout: MAX_AI_RESPONSE_TIME}
  );
  return data;
};

export const getAIInvestigateIndicesMasterDocListId = async (
  indexId: number | string
): Promise<IInvestigateNavigationMasterListResponse> => {
  const {data} = await apiClient.get<IInvestigateNavigationMasterListResponse>(
    aiInvestigateIndicesEndpoint(`/${indexId}/navigate`)
  );
  return data;
};

export const getAIInvestigateIndicesDetailMasterDocListId = async (
  indexdetailId: number | string,
  type: string
): Promise<IInvestigateNavigationMasterListResponse> => {
  const {data} = await apiClient.get<IInvestigateNavigationMasterListResponse>(
    aiInvestigateIndicesEndpoint(`/${indexdetailId}/${type}/navigate`)
  );
  return data;
};

export const getAIInvestigateTranscriptReport = async (
  reportId: number | string
) => {
  const response = await apiClient.get(
    aiInvestigateTranscriptEndpoint(`/download-csv?reportId=${reportId}`),
    {responseType: 'blob'}
  );
  const disposition = response.headers['content-disposition'];
  const match = disposition?.match(/filename\*=UTF-8''(.+)$/);
  const fileName = match ? decodeURIComponent(match[1]) : 'chatreport.csv';
  return {
    blobData: response.data,
    fileName,
  };
};
